import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useLocation as useGeoLocation } from "../lib/location";
import { toast } from "sonner";
import { Plus, Trash2, Package, Users, ClipboardList, ShieldCheck, Loader2, Wrench, Bell, CheckCircle2, Banknote, CreditCard, TrendingUp, DollarSign, Calendar, Clock, Star, Wallet, Navigation, Radio, PowerOff, Repeat, ArrowRight, Send, Car, UsersRound } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import LiveTrackingMap from "../components/LiveTrackingMap";
import ChatPanel from "../components/ChatPanel";
import DriverCard from "../components/DriverCard";
import AdminSupportInbox from "../components/AdminSupportInbox";
import AdminSupplierApprovals from "../components/AdminSupplierApprovals";
// SupplierStripeConnect component was deleted — Stripe was fully removed from Drivon.
import AdminIntegrationsCard from "../components/AdminIntegrationsCard";
import AdminFareTable from "../components/AdminFareTable";
import SupplierFleetCard from "../components/SupplierFleetCard";
import ShareReferralCard from "../components/ShareReferralCard";
import SplitCostButton from "../components/SplitCostButton";
import ReservedRidesQueue from "../components/ReservedRidesQueue";
import DriverAnalyticsCard from "../components/DriverAnalyticsCard";
import RiderEtaWidget from "../components/RiderEtaWidget";
import { formatMoney } from "../lib/money";
import { openNavigation } from "../lib/nav";

// ─── Dual-mode CTA: a passenger can unlock driver capabilities without
// creating a second account. Small dialog with the minimum-viable driver
// onboarding (category + optional plate). On success we refresh /auth/me so
// the "Switch to Driver mode" button appears in place of this CTA.
function BecomeDriverCta() {
  const { t } = useTranslation();
  const { refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("taxi");
  const [plate, setPlate] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/me/enable-driver", {
        primary_category: category,
        vehicle: plate.trim() ? { plate: plate.trim() } : undefined,
      });
      toast.success(t("becomeDriver.success", "You're a driver now! Admin will review your account within 24h."));
      if (refresh) await refresh();
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className="pill-btn rounded-full px-4 py-2.5 text-[12px] font-bold inline-flex items-center gap-2 bg-white/[0.06] border border-white/10 text-white hover:bg-white/10 transition-colors"
          data-testid="become-driver-cta"
        >
          <Car size={14} /> {t("becomeDriver.cta", "Drive with Drivons")}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-obsidian border border-white/10 text-white max-w-sm" data-testid="become-driver-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-black">
            {t("becomeDriver.title", "Turn your car into income")}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-white/60">
          {t("becomeDriver.subtitle", "One account, both modes. Book rides as a passenger and earn as a driver — no second signup needed. Admin reviews new drivers within 24h.")}
        </p>
        <div className="space-y-3 mt-3">
          <label className="block">
            <span className="text-[10px] uppercase font-black tracking-widest text-white/40">
              {t("becomeDriver.categoryLabel", "Service category")}
            </span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1 w-full rounded-xl bg-black/40 border border-white/10 px-3 py-2 text-sm text-white outline-none focus:border-electric-teal"
              data-testid="become-driver-category"
            >
              <option value="taxi">🚕 Taxi</option>
              <option value="delivery">📦 Delivery</option>
              <option value="moving">📦 Moving & Relocation</option>
              <option value="towing">🚚 Towing</option>
              <option value="mechanic">🔧 Mobile Mechanic</option>
              <option value="tire">⚙️ Tire Repair</option>
              <option value="carwash">🚿 Car Wash</option>
              <option value="bus">🚌 Bus Trip</option>
              <option value="crane">🏗️ Crane</option>
              <option value="luxury">🚗 Luxury Chauffeur</option>
              <option value="limousine">🚙 Limousine</option>
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase font-black tracking-widest text-white/40">
              {t("becomeDriver.plateLabel", "Vehicle plate (optional — you can add it later)")}
            </span>
            <input
              value={plate}
              onChange={(e) => setPlate(e.target.value)}
              placeholder="ABC-1234"
              className="mt-1 w-full rounded-xl bg-black/40 border border-white/10 px-3 py-2 text-sm text-white outline-none focus:border-electric-teal"
              data-testid="become-driver-plate"
            />
          </label>
        </div>
        <button
          onClick={submit}
          disabled={busy}
          className="mt-4 w-full rounded-full bg-electric-teal text-obsidian px-4 py-3 text-sm font-black inline-flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-electric-teal/90 transition"
          data-testid="become-driver-submit"
        >
          {busy && <Loader2 size={14} className="animate-spin" />}
          {t("becomeDriver.submit", "Unlock driver mode")}
        </button>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_COLORS = {
  scheduled: "bg-purple-500/20 text-purple-300 border border-purple-500/30",
  pending: "bg-amber-500/20 text-amber-300 border border-amber-500/30",
  accepted: "bg-blue-500/20 text-blue-300 border border-blue-500/30",
  in_progress: "bg-orange-500/20 text-orange-300 border border-orange-500/30",
  completed: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30",
  cancelled: "bg-red-500/20 text-red-300 border border-red-500/30",
};

const isScheduled = (o) => (
  o.status === "scheduled" ||
  (o.scheduled_at && new Date(o.scheduled_at) > new Date() && ["pending", "accepted"].includes(o.status))
);

export function CustomerDashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [reviewedIds, setReviewedIds] = useState(new Set());
  const load = () => api.get("/my/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);
  const upcoming = orders.filter(isScheduled);
  const active = orders.filter((o) => ["accepted", "in_progress"].includes(o.status) && !isScheduled(o)).length;
  const done = orders.filter((o) => o.status === "completed").length;

  // Quick Reorder: pick up to 3 most-recent unique listings the customer used
  const reorderable = [];
  const seenListings = new Set();
  for (const o of orders) {
    if (o.status === "cancelled") continue;
    if (!o.listing_id || seenListings.has(o.listing_id)) continue;
    seenListings.add(o.listing_id);
    reorderable.push(o);
    if (reorderable.length >= 3) break;
  }

  return (
    <DashShell title={t("dashboard.myRequests")} role="CUSTOMER" subtitle={user?.name}>
      <RiderEtaWidget />
      <div className="mb-5 flex gap-2 flex-wrap">
        <Link to="/wallet" className="pill-btn bg-white text-black rounded-full px-4 py-2.5 text-[12px] font-bold inline-flex items-center gap-2" data-testid="wallet-link">
          <Wallet size={14} /> {t("dashboard.walletCta")}
        </Link>
        <Link
          to="/shared"
          className="pill-btn rounded-full px-4 py-2.5 text-[12px] font-bold inline-flex items-center gap-2 bg-white/[0.06] border border-white/10 text-white hover:bg-white/10 transition-colors"
          data-testid="shared-ride-cta"
        >
          <UsersRound size={14} /> {t("dashboard.sharedRideCta", "Shared ride")}
        </Link>
        {!(user?.capabilities || []).includes("supplier") && (
          <BecomeDriverCta />
        )}
        {(user?.capabilities || []).includes("supplier") && (
          <Link
            to="/supplier"
            className="pill-btn rounded-full px-4 py-2.5 text-[12px] font-bold inline-flex items-center gap-2 bg-white/[0.06] border border-white/10 text-white hover:bg-white/10 transition-colors"
            data-testid="switch-to-driver-mode"
          >
            <Car size={14} /> {t("dashboard.switchToDriver", "Switch to Driver mode")} <ArrowRight size={12} className="rtl-flip" />
          </Link>
        )}
      </div>
      <Stats items={[
        { k: t("dashboard.upcoming"), v: upcoming.length, icon: Calendar, accent: upcoming.length > 0 ? "text-brand-warn" : "" },
        { k: t("dashboard.active"), v: active, icon: Bell, accent: active > 0 ? "text-brand-accent" : "" },
        { k: t("dashboard.completed"), v: done, icon: CheckCircle2 },
      ]} />

      {reorderable.length > 0 && (
        <div className="mt-6" data-testid="quick-reorder-section">
          <SectionHead label={t("dashboard.quickReorder", "Quick Reorder")} />
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {reorderable.map((o) => (
              <QuickReorderCard key={o.order_id} order={o} onDone={load} />
            ))}
          </div>
        </div>
      )}

      {upcoming.length > 0 && (
        <div className="mt-6">
          <SectionHead label={t("dashboard.upcomingSection")} />
          <div className="mt-3 bg-ink-surface rounded-2xl border border-ink-border divide-y divide-ink-border" data-testid="upcoming-orders">
            {upcoming.map((o) => <OrderRow key={o.order_id} o={o} onChange={load} role="customer" />)}
          </div>
        </div>
      )}

      <div className="mt-6">
        <SectionHead label={t("dashboard.recent")} />
        {orders.length === 0 ? (
          <div className="bg-ink-surface border border-ink-border rounded-2xl p-12 text-center mt-3">
            <div className="text-2xl font-black">{t("dashboard.noRequests")}</div>
            <Link to="/services" className="inline-block mt-4 bg-white text-black rounded-full px-5 py-2.5 text-xs font-black" data-testid="browse-services-cta">{t("dashboard.browse")}</Link>
          </div>
        ) : (
          <div className="mt-3 bg-ink-surface rounded-2xl border border-ink-border divide-y divide-ink-border" data-testid="orders-list">
            {orders.map((o) => <OrderRow key={o.order_id} o={o} onChange={load} role="customer" />)}
          </div>
        )}
      </div>
    </DashShell>
  );
}

export function SupplierDashboard() {
  const { t } = useTranslation();
  const [listings, setListings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [unread, setUnread] = useState(0);
  const load = async () => {
    const [l, o, n] = await Promise.all([
      api.get("/my/listings"),
      api.get("/my/orders"),
      api.get("/my/notifications").catch(() => ({ data: { unread: 0 } })),
    ]);
    setListings(l.data); setOrders(o.data); setUnread(n.data.unread || 0);
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const pending = orders.filter((o) => o.status === "pending").length;
  const upcoming = orders.filter(isScheduled).length;
  const netEarnings = orders
    .filter((o) => o.payment_status === "paid")
    .reduce((s, o) => s + (o.supplier_earnings || (o.total || 0) * 0.9), 0);

  const markAllViewed = async () => {
    const unseen = orders.filter((o) => o.status === "pending" && !o.viewed_by_supplier);
    await Promise.all(unseen.map((o) => api.post(`/orders/${o.order_id}/mark-viewed`)));
    load();
  };

  return (
    <DashShell title={t("dashboard.supplierHq")} role="SUPPLIER" badge={unread > 0 ? `${unread} ${t("dashboard.newBadge")}` : null}>
      <DriverStatusToggle />
      <ReservedRidesQueue />
      <DriverAnalyticsCard />
      <ShareReferralCard />
      <SupplierVehicleSpecsCard />
      <SupplierFleetCard />
      <Stats items={[
        { k: t("dashboard.listings"), v: listings.length, icon: Package },
        { k: t("dashboard.newRequests"), v: pending, icon: Bell, accent: pending > 0 ? "text-brand-warn" : "" },
        { k: t("dashboard.upcoming"), v: upcoming, icon: Calendar },
        { k: t("dashboard.netEarnings"), v: `$${netEarnings.toFixed(0)}`, icon: TrendingUp, accent: "text-brand-accent" },
      ]} />
      <NearbyDispatchAlerts onChange={load} />
      {unread > 0 && (
        <div className="mt-4 bg-brand-warn/10 border border-brand-warn/30 rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="notification-banner">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-brand-warn text-black flex items-center justify-center animate-pulse"><Bell size={18} /></div>
            <div>
              <div className="text-lg font-black text-brand-warn">{unread} {t("dashboard.newRequestsBanner")}</div>
              <div className="text-xs text-ink-textDim">{t("dashboard.newRequestsBannerSub")}</div>
            </div>
          </div>
          <button onClick={markAllViewed} className="bg-brand-warn text-black rounded-full px-4 py-2 text-xs font-black" data-testid="mark-viewed-btn">{t("dashboard.markAllViewed")}</button>
        </div>
      )}
      <div className="mt-6 grid lg:grid-cols-2 gap-4">
        <div>
          <SectionHead label={t("dashboard.myListings")} action={<CreateListingDialog onDone={load} />} />
          <div className="mt-3 bg-ink-surface border border-ink-border rounded-2xl divide-y divide-ink-border" data-testid="my-listings">
            {listings.length === 0 && <div className="p-8 text-center text-xs text-ink-textDim">{t("dashboard.noListings")}</div>}
            {listings.map((l) => (
              <div key={l.listing_id} className="p-4 flex items-center gap-3" data-testid={`my-listing-${l.listing_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-sm truncate">{l.title}</div>
                  <div className="text-[10px] uppercase text-ink-textDim font-bold">{t(`categories.${l.category}`)} · {l.service_area}</div>
                </div>
                <div className="text-white font-black">${l.price}</div>
                <button onClick={async () => { await api.delete(`/listings/${l.listing_id}`); load(); }} className="p-2 rounded-full hover:bg-red-500/20 text-red-400" data-testid={`delete-listing-${l.listing_id}`}><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </div>
        <div>
          <SectionHead label={t("dashboard.incoming")} />
          <div className="mt-3 bg-ink-surface border border-ink-border rounded-2xl divide-y divide-ink-border" data-testid="supplier-orders">
            {orders.length === 0 && <div className="p-8 text-center text-xs text-ink-textDim">{t("dashboard.noRequests")}</div>}
            {orders.map((o) => <OrderRow key={o.order_id} o={o} onChange={load} role="supplier" />)}
          </div>
        </div>
      </div>
    </DashShell>
  );
}

export function AdminDashboard() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [feePct, setFeePct] = useState(0.10);
  const load = async () => {
    const [s, u, o, c] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/orders"),
      api.get("/config").catch(() => ({ data: { platform_fee_pct: 0.10 } })),
    ]);
    setStats(s.data); setUsers(u.data); setOrders(o.data); setFeePct(c.data.platform_fee_pct);
  };
  useEffect(() => { load(); }, []);
  const updateFee = async (v) => {
    try { await api.patch("/admin/config", { platform_fee_pct: v }); setFeePct(v); toast.success("Fee updated"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashShell title={t("dashboard.adminConsole")} role="ADMIN">
      {/* Quick jump to the dedicated prices page */}
      <Link
        to="/admin/prices"
        className="mb-4 flex items-center justify-between rounded-2xl border border-electric-teal/30 bg-electric-teal/[0.06] px-4 py-3 hover:bg-electric-teal/[0.1]"
        data-testid="admin-prices-link"
      >
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-black text-electric-teal">{t("admin.prices.eyebrow", "Pricing engine")}</div>
          <div className="text-sm font-black text-white mt-0.5">{t("admin.prices.title", "Prices by country")}</div>
          <div className="text-[11px] text-white/60">{t("admin.prices.linkHint", "Set the base + per-km + per-min rate for every country you serve")}</div>
        </div>
        <div className="text-white/60 font-black text-lg">→</div>
      </Link>
      {/* NEW: Pricing Engine V2 quick-jump (iter65). Behind feature flag on backend; UI always visible for admins. */}
      <Link
        to="/admin/pricing-v2"
        className="mb-4 flex items-center justify-between rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.06] px-4 py-3 hover:bg-emerald-500/[0.1]"
        data-testid="admin-pricing-v2-link"
      >
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-black text-emerald-400">Pricing V2 · Preview</div>
          <div className="text-sm font-black text-white mt-0.5">Dynamic pricing engine</div>
          <div className="text-[11px] text-white/60">Country → Zone → Category → Option rules · with commission split</div>
        </div>
        <div className="text-emerald-400 font-black text-lg">→</div>
      </Link>
      {stats && (
        <Stats items={[
          { k: t("dashboard.users"), v: stats.users, icon: Users },
          { k: t("dashboard.suppliers"), v: stats.suppliers, icon: Wrench },
          { k: t("dashboard.requests"), v: stats.orders, icon: ClipboardList },
          { k: t("dashboard.platformFees"), v: `$${(stats.platform_fees || 0).toFixed(0)}`, icon: DollarSign, accent: "text-brand-accent" },
        ]} />
      )}
      {/* Pricing control */}
      <div className="mt-4 bg-ink-surface border border-ink-border rounded-2xl p-4" data-testid="admin-pricing-control">
        <div className="text-[10px] uppercase font-black text-ink-textDim">PLATFORM COMMISSION</div>
        <div className="mt-2 flex items-center gap-4 flex-wrap">
          <div className="text-4xl font-black">{(feePct * 100).toFixed(0)}%</div>
          <input type="range" min="0" max="30" step="1" value={Math.round(feePct * 100)}
            onChange={(e) => setFeePct(Number(e.target.value) / 100)}
            onMouseUp={(e) => updateFee(Number(e.target.value) / 100)}
            onTouchEnd={(e) => updateFee(Number(e.target.value) / 100)}
            className="flex-1 min-w-[200px] accent-brand-accent" data-testid="fee-slider" />
          <div className="text-[10px] text-ink-textDim uppercase font-bold">applies to new orders</div>
        </div>
      </div>
      <div className="mt-6 grid lg:grid-cols-2 gap-4">
        <div>
          <SectionHead label={t("dashboard.users")} />
          <div className="mt-3 bg-ink-surface border border-ink-border rounded-2xl overflow-hidden max-h-[500px] overflow-y-auto" data-testid="admin-users">
            <table className="w-full text-xs">
              <thead className="bg-ink-surface2">
                <tr><Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Verify</Th></tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-t border-ink-border" data-testid={`admin-user-${u.user_id}`}>
                    <Td className="font-bold">{u.name}</Td>
                    <Td className="text-ink-textDim lowercase">{u.email}</Td>
                    <Td><span className="bg-brand-accent/20 text-brand-accent px-2 py-0.5 rounded-full font-black uppercase text-[10px]">{u.role}</span></Td>
                    <Td>
                      {u.verified ? <span className="text-brand-accent flex items-center gap-1"><ShieldCheck size={12} /> YES</span>
                        : <button onClick={async () => { await api.patch(`/admin/users/${u.user_id}/verify`); load(); }} className="text-white font-black" data-testid={`verify-${u.user_id}`}>{t("dashboard.verify")}</button>}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <SectionHead label={t("dashboard.allRequests")} />
          <div className="mt-3 bg-ink-surface border border-ink-border rounded-2xl overflow-hidden max-h-[500px] overflow-y-auto divide-y divide-ink-border">
            {orders.map((o) => (
              <AdminOrderRow key={o.order_id} o={o} onRefunded={load} />
            ))}
          </div>
        </div>
      </div>

      {/* Supplier approvals */}
      <div className="mt-6">
        <AdminSupplierApprovals />
      </div>

      {/* Fare table editor */}
      <div className="mt-6">
        <AdminFareTable />
      </div>

      {/* 3rd-party integrations status */}
      <div className="mt-6">
        <AdminIntegrationsCard />
      </div>

      {/* Support Inbox */}
      <div className="mt-6">
        <AdminSupportInbox />
      </div>
    </DashShell>
  );
}

function DashShell({ title, role, subtitle, badge, children }) {
  return (
    <div className="max-w-[600px] md:max-w-[900px] mx-auto px-4 pt-5 pb-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-[32px] md:text-[36px] font-black tracking-tight leading-none">{title}</h1>
          {subtitle && <div className="text-[13px] text-white/50 mt-1.5">{subtitle}</div>}
        </div>
        {badge && (
          <span className="shrink-0 mt-1 bg-brand-warn text-black text-[10px] font-black uppercase px-2 py-1 rounded-full">
            {badge}
          </span>
        )}
      </div>
      <div className="mt-5">{children}</div>
    </div>
  );
}

function Stats({ items }) {
  return (
    <div className={`grid grid-cols-${items.length <= 3 ? items.length : 2} md:grid-cols-${items.length} gap-2.5`}>
      {items.map((s) => (
        <div key={s.k} className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-3.5" data-testid={`stat-${s.k.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center"><s.icon size={14} /></div>
          <div className="text-[10px] uppercase font-semibold text-white/50 tracking-widest mt-2.5">{s.k}</div>
          <div className={`text-[22px] font-black mt-0.5 ${s.accent || ""}`}>{s.v}</div>
        </div>
      ))}
    </div>
  );
}

function SectionHead({ label, action }) {
  return (
    <div className="flex items-end justify-between gap-2">
      <div className="text-[11px] uppercase font-black tracking-widest text-ink-textDim">{label}</div>
      {action}
    </div>
  );
}
function Th({ children }) { return <th className="text-start px-3 py-2 text-[10px] uppercase font-black text-ink-textDim tracking-widest">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-2 ${className}`}>{children}</td>; }

function AdminOrderRow({ o, onRefunded }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [snapOpen, setSnapOpen] = useState(false);
  const canRefund = o.payment_status === "paid" || o.payment_status === "partially_refunded";
  const refund = async () => {
    const reason = window.prompt("Refund reason (optional):", "");
    if (reason === null) return;
    const amountRaw = window.prompt(
      `${t("dashboard.refundPrompt", "Amount to refund (leave blank for full remainder). Order total:")} ${formatMoney(o.total || o.estimate, o.currency)}`,
      ""
    );
    if (amountRaw === null) return;
    const body = { reason: reason || null };
    if (amountRaw.trim()) {
      const n = Number(amountRaw);
      if (!Number.isFinite(n) || n <= 0) return toast.error("Invalid amount");
      body.amount = n;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${o.order_id}/refund`, body);
      toast.success(`${t("dashboard.refunded", "Refunded")} ${formatMoney(data.amount, data.currency)} (${data.status})`);
      onRefunded && onRefunded();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="p-4 flex justify-between gap-3" data-testid={`admin-order-${o.order_id}`}>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase text-ink-textFaint font-bold">{o.order_id}</div>
        <div className="font-black text-sm truncate">{o.listing_title}</div>
        <div className="text-[11px] text-ink-textDim">{o.customer_name} → {o.supplier_name}</div>
        <div className="text-[10px] text-ink-textDim mt-1">
          <span className="uppercase font-black">{o.payment_method}</span> · {o.payment_status}
          {o.refunded_amount > 0 && (
            <span className="ms-1 text-red-300">· {t("dashboard.refunded", "refunded")} {formatMoney(o.refunded_amount, o.currency)}</span>
          )}
        </div>
      </div>
      <div className="text-end flex flex-col items-end gap-1">
        <div className="text-white font-black">{formatMoney(o.total || o.estimate, o.currency)}</div>
        <div className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-black ${STATUS_COLORS[o.status] || ""}`}>{o.status}</div>
        {canRefund && (
          <button
            onClick={refund}
            disabled={busy}
            className="mt-1 text-[10px] font-black uppercase px-3 py-1 rounded-full bg-red-500/20 text-red-300 border border-red-500/30 hover:bg-red-500/30 disabled:opacity-40"
            data-testid={`admin-refund-btn-${o.order_id}`}
          >
            {busy ? "…" : "Refund"}
          </button>
        )}
        {/* iter65: quick-jump to the V2 pricing snapshot for this order.
            Hidden entirely on legacy orders without a V2 snapshot? No —
            the modal itself will show the "no snapshot" state, so admins
            can still confirm that a legacy order used the old engine. */}
        <button
          onClick={() => setSnapOpen(true)}
          className="mt-1 text-[10px] font-black uppercase px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25"
          data-testid={`admin-order-snapshot-${o.order_id}`}
        >
          Pricing snapshot
        </button>
      </div>
      {snapOpen && <OrderPricingSnapshotModal orderId={o.order_id} onClose={() => setSnapOpen(false)} />}
    </div>
  );
}

/** Inline modal that fetches the pricing_v2 snapshot for one order.
 * Renders the same payload as /admin/pricing-v2 → Order Snapshot tab,
 * PLUS a side-by-side diff against today's active rules so admins can
 * spot rule drift instantly. Feature-flag agnostic. */
function OrderPricingSnapshotModal({ orderId, onClose }) {
  const [data, setData] = useState(null);
  const [diff, setDiff] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("snapshot"); // "snapshot" | "diff"
  useEffect(() => {
    Promise.all([
      api.get(`/admin/pricing-v2/orders/${orderId}/snapshot`),
      api.get(`/admin/pricing-v2/orders/${orderId}/diff`).catch(() => ({ data: null })),
    ])
      .then(([s, d]) => { setData(s.data); setDiff(d?.data); })
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [orderId]);
  const pv2 = data?.pricing_v2;
  const rule = pv2?.snapshot || {};
  const hasDiff = diff && diff.present;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-bg border border-ink-border rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="admin-order-snapshot-modal">
        <div className="border-b border-ink-border px-5 py-3.5 flex items-center justify-between">
          <div>
            <div className="text-sm font-black">Pricing Snapshot</div>
            <div className="text-[11px] text-white/50 font-mono">{orderId}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/10 text-white/70" data-testid="admin-order-snapshot-close">
            ✕
          </button>
        </div>
        {hasDiff && (
          <div className="border-b border-ink-border px-5 pt-2 flex gap-4 text-xs">
            <button onClick={() => setTab("snapshot")} className={`pb-2 border-b-2 ${tab==="snapshot" ? "border-white text-white font-bold" : "border-transparent text-white/50"}`} data-testid="snapshot-tab-snapshot">
              Snapshot
            </button>
            <button onClick={() => setTab("diff")} className={`pb-2 border-b-2 ${tab==="diff" ? "border-white text-white font-bold" : "border-transparent text-white/50"}`} data-testid="snapshot-tab-diff">
              Diff vs current
              {diff?.delta?.absolute !== null && diff?.delta?.absolute !== 0 && (
                <span className={`ms-2 px-1.5 py-0.5 rounded text-[10px] font-black ${
                  (diff.delta.absolute || 0) > 0 ? "bg-red-500/20 text-red-300" : "bg-green-500/20 text-green-300"
                }`}>{(diff.delta.absolute || 0) > 0 ? "+" : ""}{diff.delta.percent}%</span>
              )}
            </button>
          </div>
        )}
        <div className="overflow-y-auto flex-1 px-5 py-4">
          {loading && <div className="text-center py-6 text-white/50 text-sm">Loading…</div>}
          {err && <div className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 p-3 text-xs">{err}</div>}
          {data && !data.present && (
            <div className="rounded-lg border border-orange-500/30 bg-orange-500/10 text-orange-200 p-3 text-xs">
              <div className="font-bold">No V2 snapshot on this order</div>
              <div className="text-orange-200/80 mt-1">
                Booked with the legacy engine. Legacy total:
                <span className="font-mono ms-1">{data.order_total_legacy} {data.order_currency}</span>
              </div>
            </div>
          )}
          {data && data.present && tab === "snapshot" && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <MetricSmall label="Passenger"  value={`${pv2.passenger_price} ${pv2.currency}`} />
                <MetricSmall label="Driver"     value={`${pv2.driver_payout} ${pv2.currency}`} accent />
                <MetricSmall label="Commission" value={`${pv2.drivon_commission} ${pv2.currency}`} sub={`${pv2.commission_percent}%`} />
              </div>
              <div className="rounded-lg border border-ink-border bg-ink-surface2/40 p-3 text-[11px] grid grid-cols-2 gap-x-4 gap-y-1">
                <div><span className="text-white/40">Rule ID</span> <span className="font-mono text-white/80">{pv2.rule_id}</span></div>
                <div><span className="text-white/40">Version</span> <span className="text-white/80">{pv2.rule_version}</span></div>
                <div><span className="text-white/40">Level</span> <span className="text-white/80">{pv2.matched_level}</span></div>
                <div><span className="text-white/40">Formula</span> <span className="text-white/80">{rule.formula}</span></div>
                <div><span className="text-white/40">Country</span> <span className="text-white/80">{rule.country}</span></div>
                <div><span className="text-white/40">Zone</span> <span className="text-white/80">{rule.zone_id || "—"}</span></div>
                <div><span className="text-white/40">Category</span> <span className="text-white/80">{rule.category}</span></div>
                <div><span className="text-white/40">Option</span> <span className="text-white/80">{rule.option_code || "any"}</span></div>
              </div>
              <details className="rounded-lg border border-ink-border p-3 text-[11px]">
                <summary className="cursor-pointer text-white/70 font-bold">Breakdown</summary>
                <pre className="mt-2 text-white/60 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(pv2.breakdown, null, 2)}</pre>
              </details>
              <details className="rounded-lg border border-ink-border p-3 text-[11px]">
                <summary className="cursor-pointer text-white/70 font-bold">Full rule snapshot</summary>
                <pre className="mt-2 text-white/60 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(pv2.snapshot, null, 2)}</pre>
              </details>
            </div>
          )}
          {data && data.present && tab === "diff" && diff && (
            <SnapshotDiffPanel diff={diff} />
          )}
        </div>
      </div>
    </div>
  );
}

/** Two-column comparison: booking-time snapshot vs today's engine.
 * Highlights whether rule ID / version changed and the price delta. */
function SnapshotDiffPanel({ diff }) {
  const past = diff.past || {};
  const now = diff.current || {};
  const nowMatched = now.matched;
  const cur = past.currency || now.currency;
  return (
    <div className="space-y-4" data-testid="snapshot-diff-panel">
      <div className="grid md:grid-cols-2 gap-3">
        <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-3">
          <div className="text-[10px] uppercase tracking-widest font-black text-blue-300 mb-2">Booking-time (locked)</div>
          <div className="text-2xl font-black tabular-nums">{past.passenger_price} {cur}</div>
          <div className="text-[11px] text-white/50 mt-1">
            v{past.rule_version} · {past.matched_level} · <span className="font-mono">{past.rule_id?.slice(0,14)}</span>
          </div>
        </div>
        <div className={`rounded-lg border p-3 ${nowMatched ? "border-emerald-500/30 bg-emerald-500/5" : "border-orange-500/30 bg-orange-500/5"}`}>
          <div className="text-[10px] uppercase tracking-widest font-black text-emerald-300 mb-2">Today (would price)</div>
          {nowMatched ? (
            <>
              <div className="text-2xl font-black tabular-nums">{now.passenger_price} {now.currency}</div>
              <div className="text-[11px] text-white/50 mt-1">
                v{(now.snapshot||{}).version} · {now.matched_level} · <span className="font-mono">{(now.matched_rule_id||"").slice(0,14)}</span>
              </div>
            </>
          ) : (
            <div className="text-sm text-orange-300 font-bold">No matching rule today</div>
          )}
        </div>
      </div>
      {nowMatched && diff.delta && (
        <div className={`rounded-lg border p-3 text-sm ${
          diff.delta.absolute === 0 ? "border-white/10 bg-white/[0.03] text-white/70" :
          diff.delta.absolute > 0 ? "border-red-500/30 bg-red-500/10 text-red-200" :
          "border-green-500/30 bg-green-500/10 text-green-200"
        }`}>
          <div className="flex items-center justify-between">
            <div className="font-bold">
              {diff.delta.absolute === 0 ? "No price change" :
                diff.delta.absolute > 0 ? "Price would be HIGHER today" : "Price would be LOWER today"}
            </div>
            <div className="text-lg font-black tabular-nums">
              {diff.delta.absolute > 0 ? "+" : ""}{diff.delta.absolute} {cur}
              <span className="text-xs ms-1">({diff.delta.percent > 0 ? "+" : ""}{diff.delta.percent}%)</span>
            </div>
          </div>
          <div className="text-[11px] mt-2 opacity-80">
            {diff.delta.same_rule_id ? "Same rule" : "Different rule matched today"}
            {" · "}
            {diff.delta.rule_version_changed ? `Rule bumped to v${diff.delta.current_rule_version}` : "Rule version unchanged"}
          </div>
        </div>
      )}
      <div className="rounded-lg border border-ink-border bg-ink-surface2/40 p-3 text-[11px]">
        <div className="text-white/60 font-bold mb-1">Inputs replayed (same for both sides)</div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-white/70">
          <div><span className="text-white/40">Country</span> {diff.inputs_replayed.country}</div>
          <div><span className="text-white/40">Category</span> {diff.inputs_replayed.category}</div>
          <div><span className="text-white/40">Option</span> {diff.inputs_replayed.option_code || "any"}</div>
          <div><span className="text-white/40">Distance</span> {diff.inputs_replayed.distance_km} km</div>
          <div><span className="text-white/40">Duration</span> {diff.inputs_replayed.duration_min} min</div>
          <div><span className="text-white/40">Extra stops</span> {diff.inputs_replayed.extra_stops}</div>
        </div>
      </div>
      <div className="text-[10px] text-white/40">
        The booking-time snapshot is immutable — this comparison is read-only and never overwrites the order's stored price.
      </div>
    </div>
  );
}

function MetricSmall({ label, value, sub, accent }) {
  return (
    <div className={`rounded-lg p-3 border text-start ${accent ? "bg-green-500/10 border-green-500/30" : "bg-white/5 border-ink-border"}`}>
      <div className="text-[9px] uppercase tracking-widest font-bold text-white/50">{label}</div>
      <div className="text-sm font-black tabular-nums mt-0.5">{value}</div>
      {sub && <div className="text-[10px] text-white/50">{sub}</div>}
    </div>
  );
}


function SupplierVehicleSpecsCard() {
  const { user, setUser } = useAuth();
  const { t } = useTranslation();
  const [specs, setSpecs] = useState(null);
  const [taxiType, setTaxiType] = useState(user?.vehicle?.taxi_type || "");
  const [seats, setSeats] = useState(user?.vehicle?.seating_capacity ? String(user.vehicle.seating_capacity) : "");
  const [truckBand, setTruckBand] = useState(user?.vehicle?.truck_tonnage_band || "");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/vehicle-specs").then((r) => setSpecs(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const vehicle = { ...(user?.vehicle || {}) };
      if (taxiType) vehicle.taxi_type = taxiType; else delete vehicle.taxi_type;
      if (seats) vehicle.seating_capacity = parseInt(seats, 10); else delete vehicle.seating_capacity;
      if (truckBand) vehicle.truck_tonnage_band = truckBand; else delete vehicle.truck_tonnage_band;
      const { data } = await api.patch("/me", { vehicle });
      setUser(data);
      toast.success(t("driver.specsSaved", "Vehicle details updated"));
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const v = user?.vehicle || {};
  const summary = [
    v.taxi_type && t(`taxi.${v.taxi_type}`, v.taxi_type),
    v.seating_capacity && `${v.seating_capacity} ${t("taxi.seatsShort", "seats")}`,
    v.truck_tonnage_band && t(`truck.${v.truck_tonnage_band}`, v.truck_tonnage_band),
  ].filter(Boolean).join(" · ");

  return (
    <div className="mb-4 bg-ink-surface border border-ink-border rounded-2xl p-4" data-testid="supplier-vehicle-specs-card">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase font-black text-ink-textDim tracking-widest">
            {t("driver.vehicleSpecs", "Taxi class & truck capacity")}
          </div>
          <div className="text-sm font-black text-white truncate mt-0.5" data-testid="supplier-specs-summary">
            {summary || t("driver.noSpecs", "Not set — help customers pick you")}
          </div>
        </div>
        <button
          onClick={() => setOpen((o) => !o)}
          className="bg-white text-black rounded-full px-3 py-1.5 text-[11px] font-black"
          data-testid="supplier-edit-specs-btn"
        >
          {open ? t("common.close", "Close") : t("common.edit", "Edit")}
        </button>
      </div>
      {open && (
        <div className="mt-3 space-y-3 pt-3 border-t border-ink-border">
          <div>
            <div className="text-[10px] uppercase font-black text-ink-textDim mb-1">
              {t("auth.taxiClass", "Taxi class")}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setTaxiType("")} className={`rounded-xl p-2 border-2 text-start ${!taxiType ? "border-white bg-white/10" : "border-ink-border"}`} data-testid="supplier-taxi-none">
                <div className="text-[11px] font-black">—</div>
              </button>
              {(specs?.taxi?.types || []).map((tt) => (
                <button
                  key={tt.id}
                  type="button"
                  onClick={() => { setTaxiType(tt.id); if (!seats) setSeats(String(tt.seats)); }}
                  className={`rounded-xl p-2 border-2 text-start ${taxiType === tt.id ? "border-white bg-white/10" : "border-ink-border"}`}
                  data-testid={`supplier-taxi-${tt.id}`}
                >
                  <div className="text-[11px] font-black text-white">{t(`taxi.${tt.id}`, tt.id)}</div>
                  <div className="text-[10px] text-ink-textDim">×{tt.multiplier.toFixed(1)}</div>
                </button>
              ))}
            </div>
          </div>
          <label className="block">
            <div className="text-[10px] uppercase font-black text-ink-textDim mb-1">
              {t("auth.seatingCapacity", "Seating capacity")}
            </div>
            <input
              type="number" min="1" max="20"
              value={seats}
              onChange={(e) => setSeats(e.target.value)}
              className="w-full bg-ink-surface2 border border-ink-border rounded-xl px-3 py-2 text-sm outline-none focus:border-white"
              data-testid="supplier-seats"
            />
          </label>
          <div>
            <div className="text-[10px] uppercase font-black text-ink-textDim mb-1">
              {t("auth.truckTonnage", "Truck capacity")}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setTruckBand("")} className={`rounded-xl p-2 border-2 text-start ${!truckBand ? "border-white bg-white/10" : "border-ink-border"}`} data-testid="supplier-truck-none">
                <div className="text-[11px] font-black">—</div>
              </button>
              {(specs?.truck?.bands || []).map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => setTruckBand(b.id)}
                  className={`rounded-xl p-2 border-2 text-start ${truckBand === b.id ? "border-white bg-white/10" : "border-ink-border"}`}
                  data-testid={`supplier-truck-${b.id}`}
                >
                  <div className="text-[11px] font-black text-white">{t(`truck.${b.id}`, b.id)}</div>
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={save}
            disabled={busy}
            className="w-full bg-white text-black rounded-full py-2.5 text-xs font-black disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="supplier-save-specs-btn"
          >
            {busy && <Loader2 size={12} className="animate-spin" />}
            {t("common.save", "Save")}
          </button>
        </div>
      )}
    </div>
  );
}

function DriverStatusToggle() {
  const { user, setUser } = useAuth();
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const online = user?.is_online !== false; // treat undefined as online (legacy)

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    const next = !online;
    try {
      const { data } = await api.patch("/me", { is_online: next });
      setUser(data);
      toast.success(next
        ? t("driver.nowOnline", "You're online — receiving new requests")
        : t("driver.nowOffline", "You're offline — no new requests"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`mb-4 rounded-2xl p-4 border-2 flex items-center gap-3 flex-wrap ${online ? "bg-brand-accent/10 border-brand-accent/50" : "bg-red-500/10 border-red-500/40"}`}
      data-testid="driver-status-toggle"
    >
      <div className={`w-11 h-11 rounded-full flex items-center justify-center ${online ? "bg-brand-accent text-black" : "bg-red-500 text-white"}`}>
        {online ? <Radio size={18} className="animate-pulse" /> : <PowerOff size={18} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-black ${online ? "text-brand-accent" : "text-red-300"}`} data-testid="driver-status-label">
          {online ? t("driver.online", "Available") : t("driver.offline", "Unavailable")}
        </div>
        <div className="text-[11px] text-ink-textDim">
          {online
            ? t("driver.onlineDesc", "Nearby customers can see your car and request rides.")
            : t("driver.offlineDesc", "You're off-duty. No dispatch notifications will be sent.")}
        </div>
      </div>
      <button
        onClick={toggle}
        disabled={busy}
        className={`rounded-full px-4 py-2 text-xs font-black inline-flex items-center gap-1 disabled:opacity-50 ${online ? "bg-red-500 text-white" : "bg-brand-accent text-black"}`}
        data-testid="driver-toggle-btn"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : (online ? <PowerOff size={12} /> : <Radio size={12} />)}
        {online ? t("driver.goOffline", "Go offline") : t("driver.goOnline", "Go online")}
      </button>
    </div>
  );
}


function NearbyDispatchAlerts({ onChange }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [upcoming, setUpcoming] = useState([]); // scheduled rides driver was early-notified about
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/dispatch/notifications");
      setItems(data);
    } catch (_) {}
  };
  useEffect(() => {
    load();
    const tmr = setInterval(load, 8000);
    // Real-time WS: refetch immediately on new_dispatch push (poll kept as fallback).
    let ws = null;
    try {
      const token = localStorage.getItem("haulhub_token");
      if (token) {
        const wsUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws") + `/api/ws/dispatch?token=${encodeURIComponent(token)}`;
        ws = new WebSocket(wsUrl);
        ws.onmessage = (ev) => {
          try {
            const m = JSON.parse(ev.data);
            if (m?.type === "new_dispatch") load();
            if (m?.type === "scheduled_ride_upcoming" && m.order_id) {
              setUpcoming((prev) => prev.some((x) => x.order_id === m.order_id) ? prev : [m, ...prev].slice(0, 5));
            }
          } catch (_) {}
        };
      }
    } catch (_) {}
    return () => {
      clearInterval(tmr);
      if (ws) try { ws.close(); } catch (_) {}
    };
  }, []);

  const reserve = async (u) => {
    setBusy(`res-${u.order_id}`);
    try {
      await api.post(`/orders/${u.order_id}/reserve`);
      toast.success(t("dispatch.reserved", "Reserved! You'll get this ride when it dispatches."));
      setUpcoming((prev) => prev.map((x) => x.order_id === u.order_id ? { ...x, reserved: true } : x));
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reserve");
    } finally {
      setBusy(null);
    }
  };

  const accept = async (n) => {
    setBusy(n.notification_id);
    try {
      await api.post(`/dispatch/${n.notification_id}/accept`);
      toast.success(t("dispatch.accepted", "Ride accepted!"));
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to accept");
      await load();
    } finally {
      setBusy(null);
    }
  };
  const decline = async (n) => {
    setBusy(n.notification_id);
    try {
      await api.post(`/dispatch/${n.notification_id}/decline`);
      await load();
    } catch (_) {}
    finally {
      setBusy(null);
    }
  };

  if (items.length === 0 && upcoming.length === 0) return null;

  return (
    <>
      {/* Scheduled Ride heads-up — early-notified 15 min before dispatch so
          drivers can reserve exclusively. */}
      {upcoming.length > 0 && (
        <div className="mt-4 bg-electric-teal/10 border border-electric-teal/40 rounded-2xl p-4" data-testid="upcoming-scheduled-alerts">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-full bg-electric-teal text-obsidian flex items-center justify-center animate-pulse"><Clock size={16} /></div>
            <div>
              <div className="text-sm font-black text-electric-teal">{t("dispatch.upcomingTitle", "Scheduled rides you can reserve")} ({upcoming.length})</div>
              <div className="text-[10px] text-ink-textDim">{t("dispatch.upcomingSubtitle", "First to reserve gets the ride — no more racing")}</div>
            </div>
          </div>
          <div className="space-y-2">
            {upcoming.map((u) => (
              <div key={u.order_id} className="bg-ink-surface border border-ink-border rounded-xl p-3 flex flex-wrap items-center gap-3" data-testid={`upcoming-${u.order_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase text-ink-textFaint font-black">{t(`categories.${u.category || 'taxi'}`)} · {t("dispatch.in", "in")} ~{u.lead_min || 15} {t("request.minShort", "min")}</div>
                  <div className="text-[11px] text-ink-textDim truncate">📍 {u.pickup_address || "—"}</div>
                  {u.scheduled_at && (
                    <div className="text-[10px] text-white/50 mt-0.5">
                      {new Date(u.scheduled_at).toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => reserve(u)}
                  disabled={busy === `res-${u.order_id}` || u.reserved}
                  className={`rounded-full text-xs font-black px-4 py-2 transition ${
                    u.reserved
                      ? "bg-electric-teal/20 text-electric-teal border border-electric-teal/40"
                      : "bg-electric-teal text-obsidian hover:bg-electric-teal/90"
                  } disabled:opacity-60`}
                  data-testid={`reserve-${u.order_id}`}
                >
                  {u.reserved ? t("dispatch.reserved", "Reserved ✓") : t("dispatch.reserve", "Reserve")}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {items.length > 0 && (
    <div className="mt-4 bg-brand-accent/10 border border-brand-accent/40 rounded-2xl p-4" data-testid="nearby-dispatch-alerts">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-full bg-brand-accent text-black flex items-center justify-center animate-pulse"><Bell size={16} /></div>
        <div>
          <div className="text-sm font-black text-brand-accent">{t("dispatch.title", "Nearby ride requests")} ({items.length})</div>
          <div className="text-[10px] text-ink-textDim">{t("dispatch.subtitle", "First to accept gets the ride")}</div>
        </div>
      </div>
      <div className="space-y-2">
        {items.map((n) => (
          <div key={n.notification_id} className="bg-ink-surface border border-ink-border rounded-xl p-3 flex flex-wrap items-center gap-3" data-testid={`dispatch-${n.notification_id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase text-ink-textFaint font-black">{t(`categories.${n.order?.category}`)} · {n.distance_km} km away</div>
              <div className="text-sm font-black truncate">{n.order?.listing_title || "Ride request"}</div>
              <div className="text-[11px] text-ink-textDim truncate">📍 {n.order?.pickup_address || "—"}{n.order?.dropoff_address ? ` → ${n.order.dropoff_address}` : ""}</div>
              <div className="text-[11px] text-brand-accent font-black mt-0.5">{formatMoney(n.order?.supplier_earnings || 0, n.order?.currency)} {t("dispatch.you", "for you")}</div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => decline(n)}
                disabled={busy === n.notification_id}
                className="bg-ink-surface2 border border-ink-border text-white rounded-full px-3 py-1.5 text-[11px] font-black disabled:opacity-50"
                data-testid={`dispatch-decline-${n.notification_id}`}
              >
                {t("dispatch.decline", "Decline")}
              </button>
              <button
                onClick={() => accept(n)}
                disabled={busy === n.notification_id}
                className="bg-brand-accent text-black rounded-full px-3 py-1.5 text-[11px] font-black inline-flex items-center gap-1 disabled:opacity-50"
                data-testid={`dispatch-accept-${n.notification_id}`}
              >
                {busy === n.notification_id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                {t("dispatch.accept", "Accept")}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
      )}
    </>
  );
}



function RatingCard({ orderId, role }) {
  const { t } = useTranslation();
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [tip, setTip] = useState(0);
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/orders/${orderId}/ratings`).then((r) => {
      if (cancelled) return;
      // Show existing card only when THIS user already rated
      const mine = (r.data?.ratings || []).find((rt) => rt.rater_role === role) || null;
      setExisting(mine);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [orderId, role]);
  const submit = async () => {
    if (stars < 1) { toast.error(t("rating.pickStars", "Pick a star rating")); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${orderId}/rate`, {
        stars, comment: comment.trim() || undefined,
        tip_amount: role === "customer" && tip > 0 ? tip : undefined,
      });
      setExisting(data);
      toast.success(role === "customer" && tip > 0 ? t("rating.thanksTip", "Thanks — tip sent to your driver") : t("rating.thanks", "Thanks for rating"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  if (existing) {
    return (
      <div className="mt-3 bg-brand-accent/10 border border-brand-accent/30 rounded-2xl p-3 text-xs" data-testid={`rating-done-${orderId}`}>
        {t("rating.yourRating", "Your rating")}: {"★".repeat(existing.stars)}{"☆".repeat(5 - existing.stars)}
        {existing.tip_amount ? ` · ${t("rating.tip", "Tip")}: ${formatMoney(existing.tip_amount, existing.currency)}` : ""}
      </div>
    );
  }
  return (
    <div className="mt-3 bg-gradient-to-br from-amber-gold/10 to-amber-gold/5 border border-amber-gold/40 rounded-2xl p-4" data-testid={`rating-card-${orderId}`}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-gold/90 mb-2">
        {role === "customer" ? t("rating.rateDriver", "Rate your driver") : t("rating.rateCustomer", "Rate your customer")}
      </div>
      <div className="flex gap-1 mb-3" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onMouseEnter={() => setHover(n)} onClick={() => setStars(n)}
            className="text-3xl transition-transform hover:scale-110" data-testid={`rating-star-${n}`}>
            <span className={n <= (hover || stars) ? "text-amber-gold" : "text-white/20"}>★</span>
          </button>
        ))}
      </div>
      <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value.slice(0, 500))}
        placeholder={t("rating.commentPh", "Anything you'd like the other party to know? (optional)")}
        className="w-full bg-ink-bg border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-amber-gold resize-none mb-3"
        data-testid={`rating-comment-${orderId}`} />
      {role === "customer" && (
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-widest font-bold text-white/50 mb-1.5">{t("rating.addTip", "Add a tip (optional)")}</div>
          <div className="flex gap-2">
            {[0, 2, 5, 10, 20].map((amt) => (
              <button key={amt} onClick={() => setTip(amt)}
                className={`flex-1 rounded-full py-1.5 text-xs font-bold border transition-all ${tip === amt ? "bg-electric-teal text-obsidian border-electric-teal" : "bg-white/[0.04] text-white/70 border-white/10"}`}
                data-testid={`tip-${amt}`}>
                {amt === 0 ? t("rating.noTip", "No tip") : `$${amt}`}
              </button>
            ))}
          </div>
        </div>
      )}
      <button onClick={submit} disabled={busy || stars < 1}
        className="w-full bg-amber-gold text-obsidian rounded-full py-2.5 text-sm font-black inline-flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid={`rating-submit-${orderId}`}>
        {busy && <Loader2 size={13} className="animate-spin" />}
        {t("rating.submit", "Submit rating")}
      </button>
    </div>
  );
}


function PinVerifyControl({ orderId, onVerified }) {  const { t } = useTranslation();
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (pin.length !== 4) {
      toast.error(t("dashboard.pinInvalid", "Enter the 4-digit code"));
      return;
    }
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/verify-code`, { code: pin });
      toast.success(t("dashboard.tripStarted", "Trip started"));
      onVerified && onVerified();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="inline-flex items-center gap-2" data-testid={`pin-verify-${orderId}`}>
      <input
        type="tel"
        inputMode="numeric"
        maxLength={4}
        pattern="\d{4}"
        value={pin}
        onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
        placeholder={t("dashboard.pinPh", "Enter code")}
        className="w-24 bg-ink-surface2 border border-brand-accent/50 rounded-full px-3 py-1.5 text-[13px] font-mono font-bold text-center text-white tabular-nums outline-none focus:border-brand-accent"
        data-testid={`pin-input-${orderId}`}
      />
      <button
        onClick={submit}
        disabled={busy || pin.length !== 4}
        className="bg-brand-accent text-black rounded-full px-3 py-1.5 text-[11px] font-black inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={`pin-submit-${orderId}`}
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />} {t("dashboard.startJob", "Start trip")}
      </button>
    </div>
  );
}

function OrderRow({ o, onChange, role }) {
  const { t } = useTranslation();
  const [driverProfile, setDriverProfile] = useState(null);
  const [verificationCode, setVerificationCode] = useState(null);
  const [verifiedAt, setVerifiedAt] = useState(o.verified_at || null);
  const showDriverToCustomer = role === "customer" && ["accepted", "in_progress", "completed"].includes(o.status) && o.supplier_id;

  useEffect(() => {
    if (!o.supplier_id) return;
    if (!["accepted", "in_progress", "completed"].includes(o.status)) return;
    let cancelled = false;
    api.get(`/orders/${o.order_id}/dispatch-status`)
      .then((r) => {
        if (cancelled) return;
        if (role === "customer") setDriverProfile(r.data?.driver_profile || null);
        setVerificationCode(r.data?.verification_code || null);
        setVerifiedAt(r.data?.verified_at || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [o.order_id, o.status, o.supplier_id, role]);
  const update = async (status) => {
    try { await api.patch(`/orders/${o.order_id}/status`, { status }); toast.success(status); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const markCashPaid = async () => {
    try { await api.post(`/orders/${o.order_id}/mark-cash-paid`); toast.success("Cash received"); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const isNew = role === "supplier" && o.status === "pending" && !o.viewed_by_supplier;
  const isCash = o.payment_method === "cash";
  const isCard = o.payment_method === "card";
  const isWallet = o.payment_method === "wallet";
  const isPaypal = o.payment_method === "paypal";
  const isPaid = o.payment_status === "paid";
  const PayIcon = isCash ? Banknote : (isWallet ? Wallet : CreditCard);
  const paidLabel = isCash ? t("payment.cashPaid") : (isWallet ? t("payment.walletPaid", "Wallet Paid") : (isPaypal ? "PayPal Paid" : t("payment.cardPaid")));
  const pendingLabel = isCash ? t("payment.awaitingCash") : (isWallet ? t("payment.walletPending", "Wallet pending") : (isPaypal ? "Awaiting PayPal" : t("payment.awaitingCard")));
  const supplierGets = o.supplier_earnings || (o.total || 0) * 0.9;
  const scheduled = o.scheduled_at ? new Date(o.scheduled_at) : null;
  const isFuture = scheduled && scheduled > new Date();
  const isTrackable = ["accepted", "in_progress"].includes(o.status);

  return (
    <div className={`p-4 relative ${isNew ? "bg-brand-warn/5" : ""}`} data-testid={`order-${o.order_id}`}>
      {isNew && <div className="absolute start-0 top-0 bottom-0 w-1 bg-brand-warn" />}
      <div className="flex justify-between items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[10px] uppercase text-ink-textFaint font-bold">{o.order_id}</div>
            {isNew && <span className="bg-brand-warn text-black text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full">{t("dashboard.newBadge")}</span>}
            {isFuture && <span className="bg-blue-500/20 text-blue-300 text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full flex items-center gap-1 border border-blue-500/30"><Calendar size={9} /> {t("dashboard.scheduled")}</span>}
            {role === "supplier" && o.reserved_by_supplier_id && (
              <span
                className="bg-electric-teal/20 text-electric-teal text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full flex items-center gap-1 border border-electric-teal/40"
                data-testid={`reserved-ribbon-${o.order_id}`}
                title={t("dashboard.reservedForYouTip", "You reserved this scheduled ride — it won't be offered to anyone else.")}
              >
                <CheckCircle2 size={9} strokeWidth={3} /> {t("dashboard.reservedForYou", "Reserved for you")}
              </span>
            )}
          </div>
          <div className="font-black">{o.listing_title}</div>
          <div className="text-xs text-ink-textDim mt-1">
            {role === "customer" ? `${t("dashboard.supplierLabel")}: ${o.supplier_name}` : `${t("dashboard.customerLabel")}: ${o.customer_name}`}
          </div>
          <div className="text-[11px] text-ink-textDim mt-1">📍 {o.pickup_address}{o.dropoff_address ? ` → ${o.dropoff_address}` : ""}</div>
          {Array.isArray(o.stops) && o.stops.length > 0 && (
            <div className="text-[11px] text-ink-textDim mt-1 flex items-center gap-1 flex-wrap" data-testid={`stops-list-${o.order_id}`}>
              <span className="text-[9px] uppercase font-black text-brand-accent tracking-widest">
                {t("dashboard.stops", "Stops")}:
              </span>
              {o.stops.map((s, i) => (
                <span key={i} className="bg-ink-surface2 border border-ink-border rounded-full px-2 py-0.5 text-[10px]">
                  {i + 1}. {s.address || `${(s.lat || 0).toFixed(3)},${(s.lng || 0).toFixed(3)}`}
                </span>
              ))}
            </div>
          )}
          {scheduled && (
            <div className="text-[11px] text-blue-300 mt-1 flex items-center gap-1"><Clock size={10} /> {scheduled.toLocaleString(undefined, { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}</div>
          )}
          {o.notes && <div className="text-[11px] text-ink-textDim mt-2 border-s-2 border-brand-warn ps-2 bg-brand-warn/5 py-1 rounded-e-lg">{o.notes}</div>}
          <div className="mt-2 flex items-center gap-2 text-[10.5px] font-black uppercase">
            <PayIcon size={12} className="text-brand-accent" />
            <span className={isPaid ? "text-brand-accent" : "text-brand-warn"} data-testid={`pay-status-${o.order_id}`}>
              {isPaid ? paidLabel : pendingLabel}
            </span>
          </div>
          {role === "supplier" && (
            <div className="mt-1 text-[10px] text-ink-textDim font-bold">
              {t("payment.supplierGets")}: <span className="text-brand-accent font-black">{formatMoney(supplierGets, o.currency)}</span>
            </div>
          )}
        </div>
        <div className="text-end">
          <div className="text-white font-black text-lg">{formatMoney(o.total || o.estimate, o.currency)}</div>
          <div className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-black ${STATUS_COLORS[o.status] || ""}`}>{o.status.replace("_", " ")}</div>
        </div>
      </div>
      {role === "supplier" && (
        <div className="mt-3 flex gap-2 flex-wrap">
          {(o.pickup_lat && o.pickup_lng) && (
            (() => {
              // Prefer deep-link into the native maps app for driver mobile
              // apps (Apple Maps on iOS, Google Maps in-app on Android).
              // Falls back to a browser tab for desktop / web.
              const hasDropoff = o.dropoff_lat != null && o.dropoff_lng != null;
              const dest = hasDropoff
                ? { lat: o.dropoff_lat, lng: o.dropoff_lng }
                : { lat: o.pickup_lat, lng: o.pickup_lng };
              return (
                <button
                  onClick={() => {
                    // For the driver, primary nav target is where the customer wants
                    // to go — if we're pre-pickup (status: pending/accepted) route to
                    // pickup first; once the trip is in_progress, route to dropoff.
                    const target = (o.status === "in_progress" && hasDropoff)
                      ? { lat: o.dropoff_lat, lng: o.dropoff_lng, label: "Drop-off" }
                      : { lat: o.pickup_lat, lng: o.pickup_lng, label: "Pickup" };
                    openNavigation(target);
                  }}
                  className="bg-brand-accent text-white rounded-full px-3 py-1.5 text-[11px] font-black inline-flex items-center gap-1"
                  data-testid={`nav-${o.order_id}`}
                  aria-label={t("dashboard.navigate", "Navigate")}
                >
                  <Navigation size={12} /> {t("dashboard.navigate", "Navigate")}
                </button>
              );
            })()
          )}
          {o.status === "pending" && <>
            <button onClick={() => update("accepted")} className="bg-blue-500 text-white rounded-full px-3 py-1.5 text-[11px] font-black" data-testid={`accept-${o.order_id}`}>{t("dashboard.accept")}</button>
            <button onClick={() => update("cancelled")} className="bg-red-500 text-white rounded-full px-3 py-1.5 text-[11px] font-black" data-testid={`decline-${o.order_id}`}>{t("dashboard.decline")}</button>
          </>}
          {o.status === "accepted" && (
            <PinVerifyControl orderId={o.order_id} onVerified={onChange} />
          )}
          {o.status === "in_progress" && <button onClick={() => update("completed")} className="bg-brand-accent text-black rounded-full px-3 py-1.5 text-[11px] font-black" data-testid={`complete-${o.order_id}`}>{t("dashboard.markComplete")}</button>}
          {isCash && !isPaid && ["accepted", "in_progress", "completed"].includes(o.status) && (
            <button onClick={markCashPaid} className="bg-white text-black rounded-full px-3 py-1.5 text-[11px] font-black inline-flex items-center gap-1" data-testid={`mark-cash-paid-${o.order_id}`}>
              <Banknote size={12} /> {t("payment.markCashPaid")}
            </button>
          )}
          {isTrackable && <ShareLocationButton orderId={o.order_id} />}
        </div>
      )}
      {role === "customer" && isTrackable && (o.pickup_lat || o.dropoff_lat) && (
        <div className="mt-3">
          <LiveTrackingMap orderId={o.order_id} height={220} />
        </div>
      )}
      {role === "customer" && ["pending", "accepted", "in_progress"].includes(o.status) && (o.stops?.length || 0) < 4 && (
        <AddStopControl order={o} onDone={onChange} />
      )}
      {showDriverToCustomer && driverProfile && (
        <DriverCard profile={driverProfile} className="mt-3" />
      )}
      {role === "customer" && verificationCode && o.status === "accepted" && (
        <div className="mt-3 bg-gradient-to-br from-amber-gold/15 to-amber-gold/5 border-2 border-amber-gold/50 rounded-2xl p-4" data-testid={`pin-card-${o.order_id}`}>
          <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-amber-gold/90">
            {t("request.showThisCode", "Show this code to your driver")}
          </div>
          <div className="mt-2 flex items-center gap-1.5 font-mono">
            {verificationCode.split("").map((d, i) => (
              <span key={i} className="inline-flex items-center justify-center w-10 h-12 bg-ink-bg border border-amber-gold/40 rounded-lg text-2xl font-bold text-white tabular-nums">{d}</span>
            ))}
          </div>
        </div>
      )}
      {role === "customer" && verifiedAt && o.status === "in_progress" && (
        <div className="mt-3 inline-flex items-center gap-1.5 bg-brand-accent/10 border border-brand-accent/40 rounded-full px-3 py-1 text-[11px] font-bold text-brand-accent" data-testid={`trip-verified-${o.order_id}`}>
          <ShieldCheck size={12} /> {t("request.tripVerified", "Trip verified")}
        </div>
      )}
      {o.status === "completed" && (
        <>
          {role === "customer" && (
            <div className="mt-3 flex flex-wrap gap-2 items-center">
              <SplitCostButton order={o} />
              {(o.splits || []).some((s) => s.status === "paid") && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] uppercase font-black px-2 py-0.5"
                  data-testid={`split-settled-ribbon-${o.order_id}`}
                  title={t("dashboard.splitSettledTip", "A friend paid their share of this ride")}
                >
                  <CheckCircle2 size={9} strokeWidth={3} /> {t("dashboard.splitSettled", "Split settled")}
                </span>
              )}
            </div>
          )}
          <RatingCard orderId={o.order_id} role={role} />
        </>
      )}
      {role === "customer" && o.status === "scheduled" && (
        <div className="mt-3 flex gap-2 flex-wrap">
          <button
            onClick={async () => {
              try {
                await api.post(`/orders/${o.order_id}/dispatch-now`);
                toast.success(t("dashboard.dispatchingNow", "Dispatching now — finding a driver…"));
                if (typeof onChange === "function") onChange();
              } catch (e) { toast.error(formatApiError(e)); }
            }}
            className="bg-brand-accent text-black rounded-full px-3 py-1.5 text-[11px] font-black inline-flex items-center gap-1"
            data-testid={`dispatch-now-${o.order_id}`}
          >
            <Send size={12} /> {t("dashboard.dispatchNow", "Dispatch now")}
          </button>
          <button
            onClick={() => update("cancelled")}
            className="bg-red-500/80 text-white rounded-full px-3 py-1.5 text-[11px] font-black"
            data-testid={`cancel-scheduled-${o.order_id}`}
          >
            {t("dashboard.cancel", "Cancel")}
          </button>
        </div>
      )}
      {role === "customer" && ["completed", "cancelled"].includes(o.status) && (
        <div className="mt-3">
          <ReorderButton order={o} />
        </div>
      )}
      {(role === "customer" || role === "supplier") && ["accepted", "in_progress"].includes(o.status) && (o.supplier_id) && (
        <ChatPanel orderId={o.order_id} peerName={role === "customer" ? o.supplier_name : o.customer_name} />
      )}
    </div>
  );
}

function AddStopControl({ order, onDone }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [address, setAddress] = useState("");
  const [coords, setCoords] = useState({ lat: null, lng: null });
  const [busy, setBusy] = useState(false);
  const { request } = useGeoLocation();

  const grabGps = async () => {
    try {
      const p = await request();
      if (p?.lat && p?.lng) {
        setCoords({ lat: p.lat, lng: p.lng });
        if (!address) setAddress(p.label || `${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}`);
      } else toast.error(t("request.locationDenied", "Location permission denied"));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    if (!coords.lat || !coords.lng) return toast.error(t("dashboard.stopNeedsCoords", "Tap GPS to pin the stop location"));
    setBusy(true);
    try {
      await api.post(`/orders/${order.order_id}/add-stop`, {
        address: address || "Stop",
        lat: coords.lat, lng: coords.lng,
      });
      toast.success(t("dashboard.stopAdded", "Stop added"));
      setOpen(false); setAddress(""); setCoords({ lat: null, lng: null });
      if (typeof onDone === "function") onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-3" data-testid={`add-stop-control-${order.order_id}`}>
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="text-[11px] uppercase font-black text-brand-accent inline-flex items-center gap-1"
          data-testid={`add-stop-open-${order.order_id}`}
        >
          <Plus size={12} /> {t("dashboard.addStop", "Add a stop")}
        </button>
      ) : (
        <div className="bg-ink-surface2 border border-ink-border rounded-xl p-3 space-y-2">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder={t("request.stopPh", "Stop address")}
              className="flex-1 bg-ink-surface border border-ink-border rounded-lg px-3 py-2 text-sm outline-none focus:border-white"
              data-testid={`add-stop-address-${order.order_id}`}
            />
            <button onClick={grabGps} className="text-[10px] uppercase font-black text-ink-textDim px-2 py-1 rounded-md border border-ink-border" data-testid={`add-stop-gps-${order.order_id}`}>
              {t("request.gps", "GPS")}
            </button>
          </div>
          {coords.lat && (
            <div className="text-[10px] text-ink-textDim">📍 {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}</div>
          )}
          <div className="flex gap-2">
            <button onClick={save} disabled={busy} className="flex-1 bg-brand-accent text-black rounded-full py-1.5 text-[11px] font-black inline-flex items-center justify-center gap-1 disabled:opacity-50" data-testid={`add-stop-save-${order.order_id}`}>
              {busy && <Loader2 size={12} className="animate-spin" />}
              {t("common.save", "Save")}
            </button>
            <button onClick={() => { setOpen(false); setAddress(""); setCoords({ lat: null, lng: null }); }} className="flex-1 bg-ink-surface border border-ink-border text-white rounded-full py-1.5 text-[11px] font-black" data-testid={`add-stop-cancel-${order.order_id}`}>
              {t("common.close", "Close")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function ReorderButton({ order, className = "" }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);

  const reorder = async () => {
    setBusy(true);
    try {
      // First: confirm the listing is still active/available
      let listing = null;
      try {
        const r = await api.get(`/listings/${order.listing_id}`);
        listing = r.data;
      } catch (_) {
        toast.error(t("dashboard.listingUnavailable", "This service is no longer available."));
        setBusy(false);
        return;
      }
      if (listing && listing.active === false) {
        toast.error(t("dashboard.listingUnavailable", "This service is no longer available."));
        setBusy(false);
        return;
      }
      // Legacy "card" payment method (from before Stripe removal) still surfaces in reorders — route to the booking page rather than silently retrying.
      if (order.payment_method === "card") {
        nav(`/book/${order.listing_id}`);
        return;
      }
      const payload = {
        listing_id: order.listing_id,
        pickup_address: order.pickup_address,
        pickup_lat: order.pickup_lat || null,
        pickup_lng: order.pickup_lng || null,
        dropoff_address: order.dropoff_address || null,
        dropoff_lat: order.dropoff_lat || null,
        dropoff_lng: order.dropoff_lng || null,
        quantity: order.quantity || 1,
        notes: order.notes || null,
        payment_method: order.payment_method || "cash",
        scheduled_at: null, // reorder is always "now"
      };
      const { data: newOrder } = await api.post("/orders", payload);
      toast.success(t("dashboard.reorderSuccess", "Reorder placed!"));
      nav("/customer");
      // Note: /customer will refetch via mount; but if we're already on /customer, force a refresh signal
      window.dispatchEvent(new CustomEvent("orders:refresh", { detail: { order_id: newOrder.order_id } }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={reorder}
      disabled={busy}
      className={`bg-white text-black rounded-full px-3 py-1.5 text-[11px] font-black inline-flex items-center gap-1 disabled:opacity-50 ${className}`}
      data-testid={`reorder-${order.order_id}`}
    >
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Repeat size={12} />}
      {t("dashboard.reorder", "Reorder")}
    </button>
  );
}

function QuickReorderCard({ order, onDone }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);

  const reorder = async () => {
    setBusy(true);
    try {
      try {
        const r = await api.get(`/listings/${order.listing_id}`);
        if (r.data?.active === false) throw new Error("inactive");
      } catch (_) {
        toast.error(t("dashboard.listingUnavailable", "This service is no longer available."));
        setBusy(false);
        return;
      }
      if (order.payment_method === "card") {
        nav(`/book/${order.listing_id}`);
        return;
      }
      const payload = {
        listing_id: order.listing_id,
        pickup_address: order.pickup_address,
        pickup_lat: order.pickup_lat || null,
        pickup_lng: order.pickup_lng || null,
        dropoff_address: order.dropoff_address || null,
        dropoff_lat: order.dropoff_lat || null,
        dropoff_lng: order.dropoff_lng || null,
        quantity: order.quantity || 1,
        notes: order.notes || null,
        payment_method: order.payment_method || "cash",
      };
      await api.post("/orders", payload);
      toast.success(t("dashboard.reorderSuccess", "Reorder placed!"));
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const payLabel = order.payment_method === "cash" ? t("payment.cash", "Cash") : order.payment_method === "wallet" ? t("payment.wallet", "Wallet") : order.payment_method === "paypal" ? "PayPal" : t("payment.card", "Card");

  return (
    <div className="bg-ink-surface border border-ink-border rounded-2xl p-3 flex items-center gap-3" data-testid={`quick-reorder-card-${order.listing_id}`}>
      <div className="w-11 h-11 rounded-xl bg-ink-surface2 flex items-center justify-center flex-shrink-0">
        <Repeat size={18} className="text-brand-accent" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[9px] uppercase text-ink-textFaint font-black tracking-wider">{t(`categories.${order.category}`)}</div>
        <div className="text-xs font-black truncate" title={order.listing_title}>{order.listing_title}</div>
        <div className="text-[10px] text-ink-textDim truncate">📍 {order.pickup_address || "—"} · {payLabel} · ${order.total || order.estimate}</div>
      </div>
      <button
        onClick={reorder}
        disabled={busy}
        className="bg-white text-black rounded-full px-3 py-2 text-[11px] font-black inline-flex items-center gap-1 disabled:opacity-50 flex-shrink-0"
        data-testid={`quick-reorder-btn-${order.listing_id}`}
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />}
        {t("dashboard.reorder", "Reorder")}
      </button>
    </div>
  );
}

function ShareLocationButton({ orderId }) {
  const { t } = useTranslation();
  const [sharing, setSharing] = useState(false);
  const [watchId, setWatchId] = useState(null);
  const [lastPush, setLastPush] = useState(null);

  const stop = () => {
    if (watchId !== null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchId);
    }
    setWatchId(null);
    setSharing(false);
  };

  const start = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by browser");
      return;
    }
    const id = navigator.geolocation.watchPosition(
      async (pos) => {
        try {
          await api.post(`/orders/${orderId}/driver-location`, {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            heading: pos.coords.heading || null,
            speed: pos.coords.speed || null,
          });
          setLastPush(new Date());
        } catch (e) {
          // 400 if order status is not trackable anymore -> auto stop
          if (e?.response?.status === 400 || e?.response?.status === 404) {
            stop();
          }
        }
      },
      (err) => {
        toast.error(err.message || "Location error");
        stop();
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
    setWatchId(id);
    setSharing(true);
    toast.success(t("dashboard.sharingLocation", "Sharing live location with customer"));
  };

  useEffect(() => () => stop(), []); // cleanup on unmount

  return (
    <button
      onClick={sharing ? stop : start}
      className={`rounded-full px-3 py-1.5 text-[11px] font-black inline-flex items-center gap-1 ${sharing ? "bg-red-500 text-white" : "bg-white text-black"}`}
      data-testid={`share-location-${orderId}`}
    >
      {sharing ? <><PowerOff size={12} /> {t("dashboard.stopSharing", "Stop Sharing")}</> : <><Radio size={12} /> {t("dashboard.shareLocation", "Share Live Location")}</>}
      {sharing && lastPush && <span className="text-[9px] opacity-80">· {Math.round((Date.now() - lastPush.getTime()) / 1000)}s</span>}
    </button>
  );
}

function RateButton({ orderId }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/reviews", { order_id: orderId, rating, comment: comment || null });
      toast.success(t("rating.thanks"));
      setDone(true); setOpen(false);
    } catch (e) {
      const msg = formatApiError(e);
      if (msg.toLowerCase().includes("already")) { setDone(true); setOpen(false); }
      else toast.error(msg);
    } finally { setBusy(false); }
  };
  if (done) return <div className="mt-3 text-[11px] text-brand-accent font-black inline-flex items-center gap-1"><Star size={12} fill="currentColor" /> {t("rating.thanks")}</div>;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="mt-3 bg-brand-warn text-black rounded-full px-3 py-1.5 text-[11px] font-black inline-flex items-center gap-1" data-testid={`rate-${orderId}`}>
          <Star size={12} /> {t("rating.rateService")}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-ink-surface border border-ink-border text-white max-w-md">
        <DialogHeader><DialogTitle className="text-xl font-black">{t("rating.title")}</DialogTitle></DialogHeader>
        <div className="text-xs text-ink-textDim -mt-2">{t("rating.subtitle")}</div>
        <div className="flex items-center justify-center gap-2 py-4">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} onClick={() => setRating(n)} className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${n <= rating ? "bg-brand-warn text-black" : "bg-ink-surface2 text-ink-textDim"}`} data-testid={`star-${n}`}>
              <Star size={22} fill={n <= rating ? "currentColor" : "none"} />
            </button>
          ))}
        </div>
        <textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)}
          placeholder={t("rating.commentPlaceholder")}
          className="w-full bg-ink-surface2 border border-ink-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-white" data-testid="rating-comment" />
        <button onClick={submit} disabled={busy} className="mt-2 w-full bg-white text-black rounded-full py-3 font-black flex items-center justify-center gap-2 disabled:opacity-50" data-testid="rating-submit">
          {busy && <Loader2 className="animate-spin" size={14} />} {t("rating.submit")}
        </button>
      </DialogContent>
    </Dialog>
  );
}

function CreateListingDialog({ onDone }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    category: "moving", title: "", description: "", price: "",
    unit: "flat", service_area: "", image_url: "",
  });
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/listings", { ...form, price: parseFloat(form.price) });
      toast.success(t("dashboard.listingCreated"));
      setOpen(false); onDone();
      setForm({ category: "moving", title: "", description: "", price: "", unit: "flat", service_area: "", image_url: "" });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="bg-white text-black rounded-full px-3 py-1.5 text-xs font-black inline-flex items-center gap-1" data-testid="new-listing-btn"><Plus size={12} /> {t("dashboard.newListing")}</button>
      </DialogTrigger>
      <DialogContent className="max-w-lg bg-ink-surface border border-ink-border text-white">
        <DialogHeader><DialogTitle className="text-xl font-black">{t("dashboard.newListingTitle")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-ink-surface2 border border-ink-border rounded-xl px-3 py-2.5 text-sm font-bold text-white" data-testid="listing-category">
            {["moving", "towing", "taxi", "mechanic", "tire", "delivery"].map((c) => <option key={c} value={c}>{t(`categories.${c}`)}</option>)}
          </select>
          <Input v={form.title} onChange={(v) => setForm({ ...form, title: v })} placeholder="Title" testid="listing-title" />
          <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" className="w-full bg-ink-surface2 border border-ink-border rounded-xl px-3 py-2.5 text-sm text-white" data-testid="listing-description" />
          <div className="grid grid-cols-2 gap-3">
            <Input v={form.price} onChange={(v) => setForm({ ...form, price: v })} placeholder="Reference price" type="number" testid="listing-price" />
            <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="w-full bg-ink-surface2 border border-ink-border rounded-xl px-3 py-2.5 text-sm font-bold text-white" data-testid="listing-unit">
              <option value="flat">Flat</option><option value="hour">Per hour</option><option value="km">Per km</option><option value="ton">Per ton</option>
            </select>
          </div>
          <Input v={form.service_area} onChange={(v) => setForm({ ...form, service_area: v })} placeholder="Service area (city, region)" testid="listing-area" />
          <Input v={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} placeholder="Image URL (optional)" testid="listing-image" />
          <button onClick={submit} disabled={busy} className="w-full bg-white text-black rounded-full py-3 font-black flex items-center justify-center gap-2 disabled:opacity-50" data-testid="submit-listing">
            {busy && <Loader2 className="animate-spin" size={14} />} {t("dashboard.newListingTitle")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Input({ v, onChange, placeholder, type = "text", testid }) {
  return <input type={type} value={v} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-ink-surface2 border border-ink-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-white text-white" data-testid={testid} />;
}
